import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AffaireService } from '../../../core/services/affaire.service';
import { ClientService } from '../../../core/services/client.service';
import { Affaire, Client, PageResponse } from '../../../core/models/models';

@Component({
  selector: 'app-affaire-create',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, RouterLink],
  templateUrl: './affaire-create.component.html',
  styleUrls: ['./affaire-create.component.css']
})
export class AffaireCreateComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private affaireService = inject(AffaireService);
  private clientService = inject(ClientService);

  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  // Client dropdown state (progressive loading + searchable)
  clientOptions = signal<Client[]>([]);
  clientSearch = signal('');
  clientPage = signal(0);
  clientTotalPages = signal(0);
  showClientDropdown = signal(false);
  isLoadingClients = signal(false);

  affaireForm = this.fb.group({
    numeroAffaire: ['', Validators.required],
    client: [null as Client | null, Validators.required]
  });

  ngOnInit() {
    this.loadClients();
  }

  toggleClientDropdown() {
    this.showClientDropdown.set(!this.showClientDropdown());
    if (this.showClientDropdown()) {
      this.resetAndLoadClients();
    }
  }

  onClientSearchChange(value: string) {
    this.clientSearch.set(value);
    this.resetAndLoadClients();
  }

  resetAndLoadClients() {
    this.clientOptions.set([]);
    this.clientPage.set(0);
    this.clientTotalPages.set(0);
    this.loadClients();
  }

  loadClients() {
    if (this.isLoadingClients()) return;
    this.isLoadingClients.set(true);
    this.clientService.getClients(this.clientSearch(), this.clientPage(), 10, 'denomination,asc')
      .subscribe({
        next: (resp: PageResponse<Client>) => {
          this.clientOptions.set([...(this.clientOptions()), ...resp.content]);
          this.clientTotalPages.set(resp.totalPages);
          this.isLoadingClients.set(false);
        },
        error: () => {
          this.isLoadingClients.set(false);
        }
      });
  }

  loadMoreClients() {
    if (this.clientPage() + 1 >= this.clientTotalPages()) return;
    this.clientPage.set(this.clientPage() + 1);
    this.loadClients();
  }

  selectClient(client: Client) {
    this.affaireForm.patchValue({ client });
    this.showClientDropdown.set(false);
  }

  displaySelectedClient(): string {
    const c = this.affaireForm.value.client as Client | null;
    return c ? `${c.denomination} (${c.numeroClient})` : 'Sélectionner un client';
  }

  onSubmit() {
    if (this.affaireForm.invalid) return;
    this.isLoading.set(true);
    this.errorMessage.set(null);

    const value = this.affaireForm.getRawValue();
    const payload: Partial<Affaire> = {
      numeroAffaire: value.numeroAffaire!,
      client: { id: (value.client as Client).id } as Client
    };

    this.affaireService.createAffaire(payload as Affaire).subscribe({
      next: () => {
        this.router.navigate(['/affaires']);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.errorMessage.set(err?.error?.message || err?.error || 'Une erreur est survenue lors de la création de l\'affaire');
      }
    });
  }
}
